import api from './api';

/**
 * Advisor Service
 * Handles all advisor-specific API calls
 */
export const advisorService = {
  // ==================== Dashboard ====================
  
  /**
   * Get advisor dashboard
   */
  async getDashboard() {
    return api.get('/advisor/dashboard');
  },

  // ==================== Profile ====================
  
  /**
   * Get advisor profile
   */
  async getProfile(userId) {
    return api.get(`/advisor/profile/${userId}`);
  },

  /**
   * Update advisor profile
   */
  async updateProfile(profileData) {
    return api.put('/advisor/profile', profileData);
  },

  // ==================== Financial Outlook Reviews ====================
  
  /**
   * Get all financial outlook reviews
   */
  async getOutlookReviews(params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/advisor/outlook-reviews?${queryString}`);
  },

  /**
   * Get outlook review by ID
   */
  async getOutlookReview(id) {
    return api.get(`/advisor/outlook-reviews/${id}`);
  },

  /**
   * Create outlook review
   */
  async createOutlookReview(reviewData) {
    return api.post('/advisor/outlook-reviews', reviewData);
  },

  /**
   * Update outlook review
   */
  async updateOutlookReview(id, reviewData) {
    return api.put(`/advisor/outlook-reviews/${id}`, reviewData);
  },

  /**
   * Publish outlook review
   */
  async publishOutlookReview(id) {
    return api.post(`/advisor/outlook-reviews/${id}/publish`);
  },

  /**
   * Delete outlook review
   */
  async deleteOutlookReview(id) {
    return api.delete(`/advisor/outlook-reviews/${id}`);
  },

  // ==================== Client Management ====================
  
  /**
   * Get all clients
   */
  async getClients(params) {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/advisor/clients?${queryString}`);
  },

  /**
   * Get client details
   */
  async getClientDetails(id) {
    return api.get(`/advisor/clients/${id}`);
  },

  /**
   * Get client summary -totalClients and totalAum
   */
  async getClientSummary(id) {
    return api.get(`/advisor/${id}/clients/summary`);
  },

  /**
   * Get client portfolio
   */
  async getClientPortfolio(id) {
    return api.get(`/advisor/clients/${id}/portfolio`);
  },

  /**
   * Add client note
   */
  async addClientNote(clientId, note) {
    return api.post(`/advisor/clients/${clientId}/notes`, note);
  },

  // ==================== Fee Structures ====================
  
  /**
   * Get fee structures
   */
  async getFeeStructures() {
    return api.get('/advisor/fee-structures');
  },

  /**
   * Create fee structure
   */
  async createFeeStructure(feeData) {
    return api.post('/advisor/fee-structures', feeData);
  },

  /**
   * Update fee structure
   */
  async updateFeeStructure(id, feeData) {
    return api.put(`/advisor/fee-structures/${id}`, feeData);
  },

  /**
   * Delete fee structure
   */
  async deleteFeeStructure(id) {
    return api.delete(`/advisor/fee-structures/${id}`);
  },

  // ==================== Service Offerings ====================
  
  /**
   * Get service offerings
   */
  async getServiceOfferings() {
    return api.get('/advisor/services');
  },

  /**
   * Update service offerings
   */
  async updateServiceOfferings(services) {
    return api.put('/advisor/services', services);
  },

  // ==================== Search Clients ====================
  
  /**
   * Search for potential clients
   */
  async searchClients(searchParams) {
    const queryString = new URLSearchParams(searchParams).toString();
    return api.get(`/advisor/clients/search?${queryString}`);
  },

  /**
   * Get connected advisors for an investor
   */
  async getConnectedAdvisors(userId) {
    return api.get(`/advisor/connected/${userId}`);
  },

  /**
   * Get connected or engaged advisors for an investor
   */
  async getConnectedOrEngagedAdvisors(userId) {
    return api.get(`/advisor/connected-engaged/${userId}`);
  },

  /**
   * Create a new advising request
   * @param {Object} requestData - AdvisingRequestDTO
   */
  async createRequest(requestData) {
    return api.post('/advising-requests', requestData);
  },

  /**
    * Get client growth and AUM analytics
    * @param {string} advisorId - The ID of the advisor
    * @param {number} startMonth - Start month (1-12)
    * @param {number} startYear - Start year
    * @param {number} endMonth - End month (1-12)
    * @param {number} endYear - End year
    */
  async getClientGrowthAndAUM(advisorId, startMonth, startYear, endMonth, endYear) {
    return api.get(`/advisor/analytics/client-growth-aum/${advisorId}?startMonth=${startMonth}&startYear=${startYear}&endMonth=${endMonth}&endYear=${endYear}`);
  },

  /**
    * Get all service requests for a user
    * @param {string} userId - The ID of the user
    */
  async getRequests(userId) {
    return api.get(`/advising-requests/user/${userId}`);
  },
};

export default advisorService;
